/**
 * Teclado numérico do app.
 *
 * A portaria digita só dígito — ID, PIN e código de barras — e o teclado do
 * sistema custa caro aqui: ele abre por cima da tela, empurra o layout e vem
 * com autocorreção e sugestão que não servem para nada. Tecla própria de 62 px
 * é mais rápida de acertar em pé, no escuro, com a fila andando.
 *
 * No computador o mesmo teclado atende o teclado físico: dígito digita,
 * Backspace apaga, Delete limpa, Enter confirma. Ninguém sentado ao balcão
 * clica em número com o mouse.
 */

import React, {useEffect, useRef} from 'react';
import {Pressable, Text, View} from 'react-native';
import {useIsFocused} from '@react-navigation/native';
import {Delete} from 'lucide-react-native';

import {tecladoFisico} from './tecladoFisico';
import {txt, useIdioma} from './idioma';
import {fontes, useTema} from './tema';

const DIGITOS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];

function emCampoDeTexto(alvo: EventTarget | null): boolean {
  const el = alvo as HTMLElement | null;
  return Boolean(el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable));
}

export function Teclado({
  aoDigitar,
  aoApagar,
  aoLimpar,
  aoConfirmar,
  altura = 62,
}: {
  aoDigitar: (digito: string) => void;
  aoApagar: () => void;
  aoLimpar: () => void;
  /** Enter do teclado físico. */
  aoConfirmar?: () => void;
  /**
   * Altura da tecla. O padrão de 62 é o da portaria, onde a fila anda e errar a
   * tecla custa caro — não diminua ali. O login é digitado uma vez por turno,
   * sentado, e cabe com menos.
   */
  altura?: number;
}) {
  const t = useTema();
  useIdioma();
  const emFoco = useIsFocused();

  // As funções do pai mudam a cada renderização; o ouvinte lê sempre a última.
  const acoes = useRef({aoDigitar, aoApagar, aoLimpar, aoConfirmar});
  acoes.current = {aoDigitar, aoApagar, aoLimpar, aoConfirmar};

  useEffect(() => {
    // Tela de baixo na pilha não digita: só a que está na frente.
    if (!emFoco) {
      return;
    }
    tecladoFisico.tomar();

    const aoTeclar = (e: KeyboardEvent) => {
      if (emCampoDeTexto(e.target) || e.ctrlKey || e.metaKey || e.altKey) {
        return;
      }
      const a = acoes.current;
      if (/^[0-9]$/.test(e.key)) {
        a.aoDigitar(e.key);
      } else if (e.key === 'Backspace') {
        a.aoApagar();
      } else if (e.key === 'Delete') {
        a.aoLimpar();
      } else if (e.key === 'Enter' && a.aoConfirmar) {
        a.aoConfirmar();
      } else {
        return;
      }
      // A tecla é deste teclado, e de mais ninguém. Sem isto o Enter também
      // "aperta" o botão com foco — que no "Digitar" abre no X, e fechava o
      // modal jogando fora o código digitado.
      e.preventDefault();
      e.stopPropagation();
    };

    // Na captura, antes de qualquer botão da tela ver a tecla.
    window.addEventListener('keydown', aoTeclar, {capture: true});
    return () => {
      tecladoFisico.soltar();
      window.removeEventListener('keydown', aoTeclar, {capture: true});
    };
  }, [emFoco]);

  const tecla = (conteudo: React.ReactNode, aoTocar: () => void, chave: string, nua = false) => (
    <Pressable
      key={chave}
      onPress={aoTocar}
      style={({pressed}) => ({
        width: '31.5%',
        height: altura,
        borderRadius: 14,
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: nua ? 'transparent' : pressed ? t.superficieAlta : t.superficie,
        borderWidth: nua ? 0 : 1,
        borderColor: t.borda,
      })}>
      {conteudo}
    </Pressable>
  );

  return (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        justifyContent: 'space-between',
        rowGap: 10,
      }}>
      {DIGITOS.map(d =>
        tecla(
          <Text
            style={{
              color: t.texto,
              fontSize: 26,
              fontFamily: fontes.semi,
              fontVariant: ['tabular-nums'],
            }}>
            {d}
          </Text>,
          () => aoDigitar(d),
          d,
        ),
      )}

      {tecla(
        <Text
          style={{
            color: t.textoFraco,
            fontSize: 12,
            fontFamily: fontes.negrito,
            letterSpacing: 1,
          }}>
          {txt('LIMPAR')}
        </Text>,
        aoLimpar,
        'limpar',
        true,
      )}

      {tecla(
        <Text
          style={{
            color: t.texto,
            fontSize: 26,
            fontFamily: fontes.semi,
            fontVariant: ['tabular-nums'],
          }}>
          0
        </Text>,
        () => aoDigitar('0'),
        '0',
      )}

      {tecla(<Delete size={26} color={t.textoFraco} strokeWidth={2} />, aoApagar, 'apagar', true)}
    </View>
  );
}
